import React, {useEffect, useState, useRef} from 'react'
import {useIntl} from 'react-intl'
import {DisplayImage} from '../../widgets/components/General/DisplayImage'
import {ToolTipUI} from '../../widgets/components/UI/ToolTipUI'
import debounce from 'lodash/debounce'
import Skeleton from 'react-loading-skeleton'
import {useThemeMode} from '../../../../_metronic/partials'
import {useNavigate} from 'react-router-dom'

export default function ShareholderSearchTextInput({
  onSelect,
  onNewEntryButtonClick,
  dropdownData,
  label,
  placeholder,
  isDisabled = false,
  openShareholderDropdown,
  dropdownDataLoading,
  setOpenShareholderDropdown,
  fieldClicked,
  toolTipText,
  isMargin,
  ariaLabel,
  onFocus,
  setStakeholderName,
  setStakeholderId,
  allFields,
  selectedToggles,
}: any) {
  const {formatMessage} = useIntl()
  const {mode} = useThemeMode()
  const [searchValue, setSearchValue] = useState('')
  const [filteredData, setFilteredData] = useState<any[]>(dropdownData)
  const navigate = useNavigate()

  const debouncedUpdateSearchQuery = debounce((value: string) => {
    if (value) {
      const results = dropdownData?.filter((shareholder: any) => {
        const companyName = shareholder?.companyName?.toLowerCase()
        const fullName = `${shareholder?.firstName ?? ''} ${shareholder?.lastName ?? ''}`
          .trim()
          .toLowerCase()
        return companyName?.includes(value.toLowerCase()) || fullName.includes(value.toLowerCase())
      })
      setFilteredData(results)
    } else {
      setFilteredData(dropdownData)
    }
  }, 300)

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setSearchValue(value)
    debouncedUpdateSearchQuery(value)
  }

  const handleShareholderClick = (shareholder: any) => {
    const name = shareholder.companyName
      ? shareholder.companyName
      : `${shareholder.firstName ?? ''} ${shareholder.lastName ?? ''}`.trim()
    setSearchValue(name)
    onSelect(shareholder.shareholderId)
    setStakeholderName(name)
    setStakeholderId(shareholder.shareholderId)
    setOpenShareholderDropdown(false)
  }

  const dropdownRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenShareholderDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`fv-row col-12 ${isMargin || 'mb-8'}`}>
      <div className='d-flex justify-content-between'>
        <label className='form-label font-size-13 text-dark text-capitalize ms-md-0'>
          {label}* <ToolTipUI tooltipText={toolTipText} />
        </label>
      </div>
      <div className='position-relative d-md-flex flex-md-column w-100'>
        <input
          className='form-control h-40px font-size-12'
          type='text'
          value={searchValue}
          onChange={handleInputChange}
          disabled={isDisabled}
          placeholder={placeholder}
          aria-label={ariaLabel}
          onFocus={onFocus}
          name={ariaLabel}
        />

        {/* Dropdown with matching shareholders */}
        {openShareholderDropdown && fieldClicked === ariaLabel && (
          <div
            className={`mt-2 ${mode === 'dark' ? 'user-dropdown-dark' : 'user-dropdown'}`}
            ref={dropdownRef}
          >
            {dropdownDataLoading ? (
              <div className='w-100 mt-0'>
                <Skeleton
                  height={150}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              </div>
            ) : (
              <div
                className={mode === 'dark' ? 'user-dropdown-content-dark' : 'user-dropdown-content'}
              >
                {filteredData?.length > 0 ? (
                  filteredData.map((shareholder: any) => (
                    <div
                      className={`d-flex flex-row align-items-center ${
                        mode === 'dark' ? 'user-info-dark' : 'user-info'
                      }`}
                      onClick={() => handleShareholderClick(shareholder)}
                      key={shareholder.shareholderId}
                    >
                      <DisplayImage
                        imgName={shareholder?.profileImage?.name}
                        height='20px'
                        width='20px'
                        fit='contain'
                        alt='profile'
                      />
                      <div className='user-names'>
                        <p className='first-name mb-0 ms-3'>
                          {shareholder.companyName
                            ? shareholder.companyName
                            : `${shareholder.firstName ?? ''} ${shareholder.lastName ?? ''}`.trim()}
                        </p>{' '}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className='no-results'>
                    <p>
                      {formatMessage({
                        id: 'No match found. Continue typing to create a new entry.',
                      })}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* New Entry button inside dropdown */}
            <hr />
            <button
              className='btn btn-primary mt-2 w-100'
              onClick={() => {
                // setOpenShareholderDropdown(false)
                // onNewEntryButtonClick()
                if (allFields && Object.keys(allFields).length > 0) {
                  localStorage.setItem('formFields', JSON.stringify(allFields))
                }
                if (selectedToggles) {
                  localStorage.setItem('selectedToggles', JSON.stringify(selectedToggles))
                }
                navigate('/stakeholders/add-stakeholder?tab=individual&iscaptible=true')
              }}
            >
              {formatMessage({id: 'New Entry'})}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
